/* eslint-disable func-names */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-syntax */
import { put, spawn, takeLatest } from 'redux-saga/effects';
import apiRequest, {
  apiSuccess,
  get,
  objectToQueryString,
  post,
  postForm,
  putForm,
} from 'utils/api/api';
import controlledCall from 'utils/services/controlledSaga';
import { appActions } from 'commons/reducer/commons.reducer';
import { history } from 'utils/history';
import { unformatNumber, unformatParse, asQuery, convertVideo, extension } from 'utils/functions';
import moment from 'moment';
import { AUCTION_AREA } from 'utils/const';
import { v4 as uuidv4 } from 'uuid';
import { LOTS_MANAGER, lotsManagerActions } from '../reducer/lotsManager.reducer';

function* initialQuery() {
  yield takeLatest(LOTS_MANAGER.INITIAL_QUERY, function* (action) {
    const { lotId, auctionId } = action;

    yield put(appActions.setLoader('lots-manager', true));
    const result = yield controlledCall(
      get,
      `api/v1/lots/manager?${asQuery({ auctionId, lotId })}`,
      undefined,
      lotsManagerActions.initialQuerySuccess,
    );

    yield put(appActions.setLoader('lots-manager', false));
    if (result?.error) {
      history.push('/lots');
    }
  });
}

function* uploadLotDocument() {
  yield takeLatest(LOTS_MANAGER.UPLOAD_LOT_DOCUMENT, function* (action) {
    const { controls } = action;
    yield put(appActions.setLoader('adding-lot-image', true));
    const data = new FormData();
    data.append('file', controls.file);
    data.append('lotId', controls.lotId);
    data.append('documentTypeCode', controls.documentTypeCode);
    const response = yield apiRequest(
      'api/v1/lot_documents/',
      {
        method: 'post',
        body: data,
        headers: {
          Accept: 'application/json',
        },
      },
      false,
    );
    yield put(appActions.setLoader('adding-lot-image', false));
    if (response) {
      yield put(lotsManagerActions.uploadLotDocumentSuccess(response));
    }
  });
}

function* destroyLotDocument() {
  yield takeLatest(LOTS_MANAGER.DESTROY_LOT_DOCUMENT, function* (action) {
    const { id } = action;
    const response = yield apiRequest(`api/v1/lot_documents/${id}`, { method: 'delete' });
    yield put(
      apiSuccess(LOTS_MANAGER.DESTROY_LOT_DOCUMENT_SUCCESS, {
        item: response,
        type: 'lotDocuments',
      }),
    );
  });
}

/**
 * Saga que crea un lote.
 */
function* createLot() {
  yield takeLatest(LOTS_MANAGER.CREATE_LOT, function* (action) {
    try {
      const { controls, imagesFiles, saveAndContinue } = action;
      yield put(appActions.setLoader('lots-manager', true));

      const data = new FormData();
      for (const file of controls.attachedFiles) {
        data.append('attachedFiles[]', file, `${file.name}`);
      }
      for (const file of imagesFiles) {
        const now = moment().valueOf().toString();
        const random = uuidv4().split('-')[0];
        data.append('imagesFiles[]', file, `${now}-${random}.${extension(file.name)}`);
      }

      // Debug: Log videoUrls antes de procesar
      // eslint-disable-next-line no-console
      //  console.log('🎥 videoUrls en controls:', controls.videoUrls);

      // Procesar videoUrls correctamente para CREATE
      let processedVideoUrls = [];
      if (controls.videoUrls && controls.videoUrls.length > 0) {
        processedVideoUrls = controls.videoUrls
          .map((video, index) => {
            const convertedUrl = convertVideo(video.url);
            // eslint-disable-next-line no-console
            console.log('🔄 Video original:', video.url, '-> Convertido:', convertedUrl);
            if (convertedUrl) {
              return {
                videoUrl: convertedUrl,
                index,
              };
            }
            return null;
          })
          .filter((video) => video !== null);
        // eslint-disable-next-line no-console
        console.log('✅ videoUrls procesados para backend:', processedVideoUrls);
      }

      // 💰 Procesar priceDetails para venta directa (área BIENES)
      let priceDetails = null;
      if (
        controls.lotType === 'sale' &&
        controls.areaId?.toString() === '1' &&
        controls.priceDetails
      ) {
        const pd = controls.priceDetails;
        // Solo enviar si hay al menos un valor definido
        if (
          pd.ownerPrice ||
          pd.netPrice ||
          pd.commissionPercentage ||
          pd.operationalCosts ||
          pd.transferFee ||
          pd.transferTax ||
          (pd.additionalCosts && pd.additionalCosts.length > 0)
        ) {
          priceDetails = {
            // Valor Mandante
            ownerPrice: parseFloat(pd.ownerPrice) || 0,
            // Valores de entrada
            commissionPercentage: parseFloat(pd.commissionPercentage) || 0,
            operationalCosts: parseFloat(pd.operationalCosts) || 0,
            transferFee: parseFloat(pd.transferFee) || 0,
            // 🆕 Impuesto de transferencia ahora es valor MANUAL
            transferTax: parseFloat(pd.transferTax) || 0,
            additionalCosts: (pd.additionalCosts || [])
              .filter((e) => e.name && e.value)
              .map((e) => ({
                name: e.name,
                value: parseFloat(e.value) || 0,
                description: e.description || null,
              })),
            // 🆕 Configuración: afecto/exento (booleano) y patente
            iva: pd.iva !== false,
            hasLicensePlate: pd.hasLicensePlate !== false,
          };
        }
      }

      const body = JSON.stringify({
        lot: {
          lotCategoryId: controls.lotCategoryId,
          description: controls.description,
          billingDescription: controls.billingDescription || '',
          index: unformatNumber(controls.index),
          fullDescription: controls.fullDescription,
          commission: controls.commission ? unformatParse(controls.commission) : null,
          sellerCommission: controls.sellerCommission
            ? unformatParse(controls.sellerCommission)
            : 0,
          auctionId: controls.auctionId,
          mandateId: controls.mandateId?.toString().trim().length === 0 ? null : controls.mandateId,
          mandateBaseId:
            controls.mandateBaseId?.toString().trim().length === 0 ? null : controls.mandateBaseId,
          newSellerId: controls.newSellerId,
          increase: controls.increase ? controls.increase : 0,
          featured: controls.featured || false,
          // Video único (para compatibilidad con BD actual)
          urlVideo:
            controls.urlVideo != null && controls.urlVideo.trim().length > 0
              ? convertVideo(controls.urlVideo)
              : null,
          utilArea: !controls.utilArea
            ? null
            : controls.builtAreaUnitId?.toString() === '2'
            ? unformatParse(controls.utilArea) * 10000
            : unformatParse(controls.utilArea),
          stateId: controls.stateId,
          ivaCode: controls.ivaCode,
          unitId: controls.unitId,
          areaUnitId: controls.areaUnitId === '' ? '1' : controls.areaUnitId,
          builtAreaUnitId: controls.builtAreaUnitId === '' ? '1' : controls.builtAreaUnitId,
          price: controls.price ? controls.price : null,
          area: !controls.area
            ? null
            : controls.areaUnitId?.toString() === '2'
            ? unformatParse(controls.area) * 10000
            : unformatParse(controls.area),
          sale: controls.lotType === 'sale' || controls.lotType === 'lease',
          lease: controls.lotType === 'lease',
          lotType: controls.lotType,
          biddingEmail: controls.lotType === 'BID' ? controls.biddingEmail : '',
          name: controls.name?.toUpperCase(),
          guaranteeAmount:
            controls.areaId === AUCTION_AREA.BIENES
              ? controls.guaranteeAmount
              : controls.guaranteeUnit === '1'
              ? controls.price * (controls.guaranteeAmount / 100)
              : controls.guaranteeAmount,
          minimumUnitId: controls.lotType === 'sale' ? controls.unitId : controls.minimumUnitId,
          brandModelId: controls.brandModelId || null,
          limitParticipants: controls.limitParticipants,
          guaranteeOfferPercentage: controls.guaranteeOfferPercentage,
          lotTypeId: controls.lotTypeId ? Number(controls.lotTypeId) : null,
          lotZoneId: controls.lotZoneId ? Number(controls.lotZoneId) : null,
        },
        // Array de múltiples videos (nuevo campo) - enviado fuera de lot
        videoUrls: processedVideoUrls,

        location: {
          address: controls.address,
          communeId: controls.communeId?.trim().length === 0 ? undefined : controls.communeId,
          regionId: controls.regionId?.trim().length === 0 ? undefined : controls.regionId,
          long: controls.long,
          lat: controls.lat,
        },
        entitiesIds: controls.entitiesIds,
        detailLines: controls.detailLines,
        lotImages: controls.lotImages,
        polygons: controls.polygons,
        priceDetails, // Agregar priceDetails al cuerpo de la solicitud
      });
      data.append('jsonBody', body);

      const result = yield controlledCall(
        postForm,
        'api/v1/lots',
        data,
        lotsManagerActions.createLotSuccess,
      );
      if (!result?.error) {
        yield put(appActions.setSuccess('Se ha creado el lote exitosamente'));
        if (!saveAndContinue) {
          history.push(
            `/lots?areaId=${controls.areaId}&auctionId=${controls.auctionId}&offset=0&page=0&limit=12&unitId=0&minRange=0&maxRange=0`,
          );
        }
      }
    } catch (error) {
      yield put(appActions.setError('Error al crear el lote. Intenta nuevamente.'));
    } finally {
      yield put(appActions.setLoader('lots-manager', false));
    }
  });
}

/**
 * Saga que edita un lote existente.
 */
function* editLot() {
  yield takeLatest(LOTS_MANAGER.EDIT_LOT, function* (action) {
    try {
      const { controls, imagesFiles, saveAndContinue } = action;
      yield put(appActions.setLoader('lots-manager', true));

      const data = new FormData();
      for (const file of controls.attachedFiles) {
        if ('size' in file) {
          data.append('attachedFiles[]', file, `${file.name}`);
        }
      }
      for (const file of imagesFiles) {
        const now = moment().valueOf().toString();
        const random = uuidv4().split('-')[0];
        data.append('imagesFiles[]', file, `${now}-${random}.${extension(file.name)}`);
      }

      // Debug: Log videoUrls antes de procesar
      // eslint-disable-next-line no-console
      console.log('🎥 videoUrls en controls:', controls.videoUrls);

      // Procesar videoUrls correctamente para EDIT
      let processedVideoUrls = null;
      if (controls.videoUrls && controls.videoUrls.length > 0) {
        processedVideoUrls = controls.videoUrls
          .map((video, index) => {
            const convertedUrl = convertVideo(video.url);
            // eslint-disable-next-line no-console
            console.log('🔄 Video original:', video.url, '-> Convertido:', convertedUrl);
            if (convertedUrl) {
              // Si tiene ID, es un video existente; si no, es nuevo
              const videoData = {
                videoUrl: convertedUrl,
                index,
              };
              if (video.id !== undefined) {
                videoData.id = video.id;
              }
              return videoData;
            }
            return null;
          })
          .filter((video) => video !== null);
        // eslint-disable-next-line no-console
        console.log('✅ videoUrls procesados para backend:', processedVideoUrls);
      }

      // 💰 Procesar priceDetails para venta directa (área BIENES)
      let priceDetails = null;
      if (
        controls.lotType === 'sale' &&
        controls.areaId?.toString() === '1' &&
        controls.priceDetails
      ) {
        const pd = controls.priceDetails;
        // Siempre enviar si es venta directa en área BIENES
        priceDetails = {
          id: pd.id || null, // ID existente para update
          // Valor Mandante
          ownerPrice: parseFloat(pd.ownerPrice) || 0,
          // Valores de entrada
          commissionPercentage: parseFloat(pd.commissionPercentage) || 0,
          operationalCosts: parseFloat(pd.operationalCosts) || 0,
          transferFee: parseFloat(pd.transferFee) || 0,
          // 🆕 Impuesto de transferencia ahora es valor MANUAL
          transferTax: parseFloat(pd.transferTax) || 0,
          additionalCosts: (pd.additionalCosts || [])
            .filter((e) => e.name)
            .map((e) => ({
              name: e.name,
              value: parseFloat(e.value) || 0,
              description: e.description || null,
            })),
          // 🆕 Configuración: afecto/exento (booleano) y patente
          iva: pd.iva !== false,
          hasLicensePlate: pd.hasLicensePlate !== false,
        };
      }

      const body = JSON.stringify({
        lot: {
          lotCategoryId: controls.lotCategoryId,
          description: controls.description,
          billingDescription: controls.billingDescription || '',
          fullDescription: controls.fullDescription,
          index: unformatNumber(controls.index),
          commission: controls.commission ? unformatParse(controls.commission) : null,
          sellerCommission: controls.sellerCommission
            ? unformatParse(controls.sellerCommission)
            : 0,
          auctionId: controls.auctionId,
          mandateId: controls.mandateId?.toString().trim().length === 0 ? null : controls.mandateId,
          mandateBaseId:
            controls.mandateBaseId?.toString().trim().length === 0 ? null : controls.mandateBaseId,
          newSellerId: controls.newSellerId,
          increase: controls.increase ? controls.increase : 0,
          featured: controls.featured || false,
          // Video único (para compatibilidad con BD actual)
          urlVideo:
            controls.urlVideo != null && controls.urlVideo.trim().length > 0
              ? convertVideo(controls.urlVideo)
              : null,
          // Array de múltiples videos (nuevo campo)
          videoUrls: processedVideoUrls,
          utilArea: !controls.utilArea
            ? null
            : controls.builtAreaUnitId?.toString() === '2'
            ? unformatParse(controls.utilArea) * 10000
            : unformatParse(controls.utilArea),
          stateId: controls.stateId,
          ivaCode: controls.ivaCode,
          unitId: controls.unitId,
          areaUnitId: controls.areaUnitId === '' ? '1' : controls.areaUnitId,
          builtAreaUnitId: controls.builtAreaUnitId === '' ? '1' : controls.builtAreaUnitId,
          price: controls.price ? controls.price : null,
          area: !controls.area
            ? null
            : controls.areaUnitId?.toString() === '2'
            ? unformatParse(controls.area) * 10000
            : unformatParse(controls.area),
          sale: controls.lotType === 'sale' || controls.lotType === 'lease',
          lease: controls.lotType === 'lease',
          lotType: controls.lotType,
          biddingEmail: controls.lotType === 'BID' ? controls.biddingEmail : '',
          name: controls.name?.toUpperCase(),
          guaranteeAmount:
            controls.areaId === AUCTION_AREA.BIENES
              ? null
              : controls.guaranteeUnit === '1'
              ? controls.price * (controls.guaranteeAmount / 100)
              : controls.guaranteeAmount,
          minimumUnitId: controls.lotType === 'sale' ? controls.unitId : controls.minimumUnitId,
          brandModelId: controls.brandModelId || null,
          limitParticipants: controls.limitParticipants,
          guaranteeOfferPercentage: controls.guaranteeOfferPercentage,
          lotTypeId: controls.lotTypeId ? Number(controls.lotTypeId) : null,
          lotZoneId: controls.lotZoneId ? Number(controls.lotZoneId) : null,
        },
        location: {
          address: controls.address,
          communeId:
            controls.communeId?.toString().trim().length === 0 ? undefined : controls.communeId,
          regionId:
            controls.regionId?.toString().trim().length === 0 ? undefined : controls.regionId,
          long: controls.long,
          lat: controls.lat,
        },
        entitiesIds: controls.entitiesIds,
        detailLines: controls.detailLines,
        lotImages: controls.lotImages,
        polygons: controls.polygons,
        deletedFilesIds: controls.deletedFilesIds,
        deletedImagesIds: controls.deletedImages,
        deletedVideoIds: controls.deletedVideoIds || [],
        videoUrls: processedVideoUrls,
        imagesMeta: controls.imagesMeta,
        priceDetails, // Agregar priceDetails al cuerpo de la solicitud
      });
      data.append('jsonBody', body);

      const result = yield controlledCall(
        putForm,
        `api/v1/lots/${controls.lotId}`,
        data,
        lotsManagerActions.editLotSuccess,
      );

      if (!result?.error) {
        yield put(appActions.setSuccess('Se ha editado el lote exitosamente'));
        if (!saveAndContinue) {
          // history.push(`/lots/${controls.lotId}`);
          history.push(
            `/lots?areaId=${controls.areaId}&auctionId=${controls.auctionId}&offset=0&page=0&limit=12&unitId=0&minRange=0&maxRange=0`,
          );
        }
      }
    } catch (error) {
      yield put(appActions.setError('Error al editar el lote. Intenta nuevamente.'));
    } finally {
      yield put(appActions.setLoader('lots-manager', false));
    }
  });
}

function* satIntegration() {
  yield takeLatest(LOTS_MANAGER.SAT_INTEGRATION, function* (action) {
    const { license } = action;
    const body = { license };
    yield put(appActions.setLoader('integrating-sat-system', true));
    yield controlledCall(
      post,
      'api/v1/services/carLicenseInfo/',
      body,
      lotsManagerActions.satIntegrationSuccess,
      lotsManagerActions.satIntegrationFail,
    );
    yield put(appActions.setLoader('integrating-sat-system', false));
  });
}

function* getHistory() {
  yield takeLatest(LOTS_MANAGER.GET_HISTORY, function* (action) {
    yield put(appActions.setLoader('get-history', true));
    yield controlledCall(
      get,
      'api/v1/lots/getHistory',
      objectToQueryString(action.controls),
      lotsManagerActions.getHistorySuccess,
      lotsManagerActions.getHistoryFail,
    );
    yield put(appActions.setLoader('get-history', false));
  });
}

function* getBasicInfo() {
  yield takeLatest(LOTS_MANAGER.GET_BASIC_INFO, function* (action) {
    yield controlledCall(
      get,
      `api/v1/lots/info/${action.id}`,
      null,
      lotsManagerActions.getBasicInfoSuccess,
      lotsManagerActions.getBasicInfoFail,
    );
  });
}

export default function* root() {
  yield spawn(initialQuery);
  yield spawn(createLot);
  yield spawn(editLot);
  yield spawn(uploadLotDocument);
  yield spawn(destroyLotDocument);
  yield spawn(satIntegration);
  yield spawn(getHistory);
  yield spawn(getBasicInfo);
}
